<template>
  <div class="font-test">
    <div class="test-section">
      <h3>阿里普惠字体测试</h3>
      <p>当前设备类型: {{ deviceType }}</p>
      <p>字体策略: {{ fontStrategy }}</p>
      <p>阿里普惠字体状态: {{ alibabaFontLoaded ? '已加载' : '未加载' }}</p>
      <p class="font-sample">这是阿里普惠字体的示例文字</p>
      <p class="font-sample-bold">这是阿里普惠字体粗体示例</p>
    </div>

    <div class="test-section">
      <h3>Element UI 图标测试</h3>
      <div class="icon-grid">
        <div class="icon-item" v-for="icon in icons" :key="icon.name">
          <i :class="icon.class"></i>
          <span>{{ icon.name }}</span>
        </div>
      </div>
    </div>

    <div class="test-section">
      <h3>字体状态</h3>
      <p>当前设备类型: {{ deviceType }}</p>
      <p>阿里普惠字体状态: {{ alibabaFontLoaded ? '已加载' : '未加载' }}</p>
      <p>字体策略: {{ fontStrategy }}</p>
      <button @click="checkFontStatus" class="btn">
        重新检测字体状态
      </button>
      <button @click="runDebugReport" class="btn btn-debug">
        运行字体调试
      </button>
      <button @click="runPathTest" class="btn btn-path">
        测试字体路径
      </button>
    </div>

    <div class="test-section" v-if="debugReport">
      <h3>调试报告</h3>
      <div class="debug-info">
        <h4>字体文件检查:</h4>
        <ul>
          <li v-for="file in debugReport.fontFiles" :key="file.file">
            {{ file.file }}: {{ file.exists ? '✅ 存在' : '❌ 不存在' }}
            <span v-if="file.error" class="error">({{ file.error }})</span>
            <span v-if="file.note" class="note">[{{ file.note }}]</span>
          </li>
        </ul>
        
        <h4>@font-face 规则:</h4>
        <ul>
          <li v-for="rule in debugReport.fontFaceRules" :key="rule.fontFamily">
            字体族: {{ rule.fontFamily }}, 字重: {{ rule.fontWeight }}
          </li>
        </ul>
        
        <h4>已加载字体:</h4>
        <ul>
          <li v-for="font in debugReport.loadedFonts" :key="font.font">
            {{ font.font }}: {{ font.loaded ? '✅ 已加载' : '❌ 未加载' }}
          </li>
        </ul>
        
        <h4>字体渲染测试:</h4>
        <p>阿里普惠字体宽度: {{ debugReport.renderingTest.alibabaWidth }}</p>
        <p>Arial字体宽度: {{ debugReport.renderingTest.arialWidth }}</p>
        <p>阿里普惠字体加载: {{ debugReport.renderingTest.alibabaLoaded ? '✅ 成功' : '❌ 失败' }}</p>
        
        <h4>修复建议:</h4>
        <ul>
          <li v-for="suggestion in fixSuggestions" :key="suggestion" class="suggestion">
            {{ suggestion }}
          </li>
        </ul>
      </div>
    </div>

    <div class="test-section" v-if="pathTestResults.length > 0">
      <h3>字体路径测试结果</h3>
      <div class="debug-info">
        <h4>可用的字体路径:</h4>
        <ul>
          <li v-for="result in workingPaths" :key="result.path" class="success">
            ✅ {{ result.path }} (状态: {{ result.status }})
          </li>
        </ul>
        
        <h4>失败的字体路径:</h4>
        <ul>
          <li v-for="result in failedPaths" :key="result.path" class="error">
            ❌ {{ result.path }} (错误: {{ result.error }})
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { fontLoader } from '@/utils/fontLoader.js'
import { fontDebugger } from '@/utils/fontDebugger.js'
import { fontPathTester } from '@/utils/fontPathTester.js'

export default {
  name: 'FontTest',
  data() {
    return {
      deviceType: '',
      alibabaFontLoaded: true,
      fontStrategy: '统一使用阿里普惠字体',
      debugReport: null,
      fixSuggestions: [],
      pathTestResults: [],
      icons: [
        { name: '左箭头', class: 'el-icon-arrow-left' },
        { name: '右箭头', class: 'el-icon-arrow-right' },
        { name: '引用', class: 'el-icon-quote-left' },
        { name: '检查', class: 'el-icon-check' },
        { name: '聊天', class: 'el-icon-chat-dot-round' },
        { name: '分享', class: 'el-icon-share' },
        { name: '关闭', class: 'el-icon-close' },
        { name: '位置', class: 'el-icon-location' }
      ]
    }
  },
  computed: {
    workingPaths() {
      return this.pathTestResults.filter(result => result.exists);
    },
    failedPaths() {
      return this.pathTestResults.filter(result => !result.exists);
    }
  },
  mounted() {
    this.updateStatus()
  },
  methods: {
    updateStatus() {
      this.deviceType = fontLoader.isMobile ? '移动端' : '桌面端'
      this.fontStrategy = '统一使用阿里普惠字体'
      this.checkAlibabaFont()
    },
    async checkAlibabaFont() {
      try {
        this.alibabaFontLoaded = await fontLoader.checkFontLoaded('Alibaba PuHuiTi')
      } catch (error) {
        console.warn('字体检测失败:', error)
        this.alibabaFontLoaded = false
      }
    },
    async checkFontStatus() {
      await this.checkAlibabaFont()
      this.updateStatus()
    },
    async runDebugReport() {
      try {
        this.debugReport = await fontDebugger.generateDebugReport()
        this.fixSuggestions = fontDebugger.getFixSuggestions(this.debugReport)
      } catch (error) {
        console.error('调试报告生成失败:', error)
      }
    },
    async runPathTest() {
      try {
        this.pathTestResults = await fontPathTester.runFullTest()
      } catch (error) {
        console.error('字体路径测试失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.font-test {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.test-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.test-section h3 {
  margin-bottom: 15px;
  color: #333;
}

.font-sample {
  font-family: 'Alibaba PuHuiTi', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  font-size: 16px;
  margin: 10px 0;
}

.font-sample-bold {
  font-family: 'Alibaba PuHuiTi', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 10px 0;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
}

.icon-item i {
  font-size: 24px;
  margin-bottom: 5px;
  color: #409EFF;
}

.icon-item span {
  font-size: 12px;
  color: #666;
}

.btn {
  background-color: #409EFF;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
  margin-bottom: 10px;
}

.btn:hover {
  background-color: #337ecc;
}

.btn-debug {
  background-color: #E6A23C;
}

.btn-debug:hover {
  background-color: #d49426;
}

.btn-path {
  background-color: #67C23A;
}

.btn-path:hover {
  background-color: #5daf34;
}

.debug-info {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-top: 15px;
}

.debug-info h4 {
  margin: 15px 0 8px 0;
  color: #333;
  font-size: 14px;
}

.debug-info ul {
  margin: 0;
  padding-left: 20px;
}

.debug-info li {
  margin-bottom: 5px;
  font-size: 13px;
}

.error {
  color: #F56C6C;
  font-style: italic;
}

.note {
  color: #909399;
  font-style: italic;
}

.suggestion {
  color: #E6A23C;
  font-weight: 500;
}

.success {
  color: #67C23A;
  font-weight: 500;
}
</style> 